import { MagicWandIcon } from '@phosphor-icons/react';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { I18nNamespace } from '@/lib/i18n-namespaces';

const GLOW_DURATION_MS = 2000;

const GLOW_GRADIENT =
    'conic-gradient(from var(--glow-angle), rgba(255,255,255,0.9), rgba(255,255,255,0.2) 20%, transparent 40%, transparent 60%, rgba(255,255,255,0.2) 80%, rgba(255,255,255,0.9))';

const BORDER_MASK =
    'radial-gradient(circle closest-side, transparent calc(100% - 1.5px), black 100%)';
interface EnhancePromptButtonProps {
    onClick: () => void;
    loading: boolean;
    visible: boolean;
}

export function EnhancePromptButton({
    onClick,
    loading,
    visible,
}: EnhancePromptButtonProps) {
    const { t } = useTranslation(I18nNamespace.COMMON);
    const [glowing, setGlowing] = useState(false);
    const wasVisible = useRef(visible);
    const hadLoadingCycle = useRef(false);
    const timerRef = useRef<ReturnType<typeof setTimeout>>(null);

    useEffect(() => {
        if (loading) {
            hadLoadingCycle.current = true;
        }
        if (!wasVisible.current && visible && !hadLoadingCycle.current) {
            setGlowing(true);
            timerRef.current = setTimeout(
                () => setGlowing(false),
                GLOW_DURATION_MS,
            );
        }
        if (!wasVisible.current && visible) {
            hadLoadingCycle.current = false;
        }
        wasVisible.current = visible;

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [visible, loading]);

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <div
                    className={`relative flex items-center justify-center transition-opacity duration-150 ${visible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                >
                    <div
                        className={`absolute inset-[-1.5px] rounded-full animate-glow-spin transition-opacity duration-1000 ${glowing ? 'opacity-80' : 'opacity-0'}`}
                        style={{
                            background: GLOW_GRADIENT,
                            WebkitMaskImage: BORDER_MASK,
                            maskImage: BORDER_MASK,
                        }}
                    />
                    <Button
                        type="button"
                        variant="ghost"
                        shape="round"
                        size="medium"
                        className={`relative size-8 min-w-8 p-0 transition-colors duration-1000 hover:text-white-100 hover:bg-grey-400 ${
                            glowing ? 'text-white-100' : 'text-grey-300'
                        }`}
                        onClick={onClick}
                        loading={loading}
                        aria-label={t('promptInput.enhanceAriaLabel')}
                        leadingIcon={<MagicWandIcon className="size-4" />}
                    />
                </div>
            </TooltipTrigger>
            <TooltipContent side="top">
                {t('promptInput.enhanceTooltip')}
            </TooltipContent>
        </Tooltip>
    );
}
